import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, faEye, faEdit, faCopy, faTrash, faPaperPlane, 
  faSearch, faFilter, faTimes, faHistory, faClipboardList, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { QuestionnaireTemplate } from '../types/questionnaire';
import { 
  getQuestionnaireTemplates, 
  deleteQuestionnaireTemplate, 
  duplicateTemplate
} from '../services/api';
import PreviewModal from '../components/questionnaires/PreviewModal';
import DeleteModal from '../components/questionnaires/DeleteModal';
import ForceDeleteModal from '../components/questionnaires/ForceDeleteModal';
import SendModal from '../components/questionnaires/SendModal';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import VersionHistory from '../components/questionnaires/VersionHistory';

const Questionnaires: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [templates, setTemplates] = useState<QuestionnaireTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>((location.state as any)?.filterType || 'all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [highlightId, setHighlightId] = useState<string | undefined>((location.state as any)?.highlightId);
  
  // Modal States
  const [showPreview, setShowPreview] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<QuestionnaireTemplate | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
  const [forceDeleteModalVisible, setForceDeleteModalVisible] = useState(false); // Neuer State
  const [deleteWarningData, setDeleteWarningData] = useState<any>(null);
  const [forceDeleteData, setForceDeleteData] = useState<any>(null); // Separate data for force delete
  const [forceDeleteTemplate, setForceDeleteTemplate] = useState<QuestionnaireTemplate | null>(null); // Separate template for force delete
  const [templateToDelete, setTemplateToDelete] = useState<QuestionnaireTemplate | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [selectedTemplateForVersions, setSelectedTemplateForVersions] = useState<QuestionnaireTemplate | null>(null);



  // Data loading - nur Templates laden
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('📊 Loading templates...');
      
      const templatesResponse = await getQuestionnaireTemplates();
      setTemplates(templatesResponse.data || []);
      
      console.log('✅ Templates loaded:', templatesResponse.data?.length || 0);
      
    } catch (err) {
      console.error('❌ Error loading data:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reload templates when returning from editor
  useEffect(() => {
    if (location.state) {
      console.log('🔄 Returning from editor, reloading templates...');
      loadData();
      // Clear location state after using it
      window.history.replaceState({}, document.title);
    }
  }, [location.state, loadData]);

  // Event handlers
  const handlePreview = useCallback((template: QuestionnaireTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedTemplate(template);
    setShowPreview(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setShowPreview(false);
    setTimeout(() => setSelectedTemplate(null), 200);
  }, []);

  const handleSendClick = useCallback((template: QuestionnaireTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedTemplate(template);
    setShowSendModal(true);
  }, []);

  const handleCloseSendModal = useCallback(() => {
    setShowSendModal(false);
    setTimeout(() => setSelectedTemplate(null), 200);
  }, []);

  const handleDelete = useCallback((template: QuestionnaireTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    
    // Prüfen ob es ein nicht löschbares System-Template ist
    if (template.isSystemTemplate || template.isDeletable === false) {
      // Hier könnte man eine Warnung anzeigen
      console.warn('System-Templates können nicht gelöscht werden');
      return;
    }
    
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setShowForceDeleteModal(false);
    setDeleteWarningData(null);
    setForceDeleteModalVisible(false);
    setForceDeleteData(null);
    setForceDeleteTemplate(null);
    setTimeout(() => setTemplateToDelete(null), 200);
  }, []);

  const handleConfirmDelete = useCallback(async (forceDelete = false) => {
    if (!templateToDelete || !templateToDelete._id) return;
    
    try {
      const url = forceDelete ? `${templateToDelete._id}?force=true` : templateToDelete._id;
      await deleteQuestionnaireTemplate(url);
      await loadData();
      
      // Nur bei erfolgreichem Delete die Modals schließen
      setShowDeleteModal(false);
      setShowForceDeleteModal(false);
      setDeleteWarningData(null);
      setTemplateToDelete(null);
      
      // Success toast
      if (forceDelete) {
        console.log('Template and related submissions deleted successfully');
      }
    } catch (error: any) {
      console.error('Error deleting template:', error);
      
      // Check if this is a 409 conflict (submissions exist)
      if (error.response?.status === 409) {
        const warningData = error.response.data;
        console.log('🔍 409 conflict detected, warningData:', warningData);
        console.log('🔍 Current state before update:', {
          showDeleteModal,
          showForceDeleteModal,
          deleteWarningData,
          templateToDelete
        });
        
        // Erst alle States zurücksetzen, dann das Force Delete Modal anzeigen
        setShowDeleteModal(false);
        setDeleteWarningData(warningData);
        
        // Sichere die Daten für das Force Delete Modal separat
        setForceDeleteData(warningData);
        setForceDeleteTemplate(templateToDelete);
        
        // Verwende den neuen State für das Force Delete Modal
        setTimeout(() => {
          setForceDeleteModalVisible(true);
          console.log('🔍 Force delete modal set to true (new state)');
        }, 10);
        
        // Noch mehr Debug
        console.log('🔍 After setState calls');
        setTimeout(() => {
          console.log('🔍 State nach 100ms:', {
            showForceDeleteModal,
            showDeleteModal,
            deleteWarningData: !!deleteWarningData,
            templateToDelete: !!templateToDelete
          });
        }, 100);
        
        console.log('🔍 Force delete modal should be visible now');
        console.warn(`Template cannot be deleted: ${warningData.submissionCount} submissions exist`);
      } else {
        console.error('Failed to delete template:', error.message);
      }
    }
  }, [templateToDelete, loadData]);

  const handleDuplicate = useCallback(async (template: QuestionnaireTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await duplicateTemplate(template._id!);
      await loadData();
    } catch (error) {
      console.error('Error duplicating template:', error);
    }
  }, [loadData]);

  const handleShowVersionHistory = useCallback((template: QuestionnaireTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedTemplateForVersions(template);
    setShowVersionHistory(true);
  }, []);

  const handleCloseVersionHistory = useCallback(() => {
    setShowVersionHistory(false);
    setTimeout(() => setSelectedTemplateForVersions(null), 200);
  }, []);

  const handleVersionRestored = useCallback((newTemplate: QuestionnaireTemplate) => {
    loadData();
    setShowVersionHistory(false);
    setTimeout(() => setSelectedTemplateForVersions(null), 200);
  }, [loadData]);

  const handleTemplateClick = useCallback((template: QuestionnaireTemplate) => {
    navigate(`/questionnaires/edit/${template._id}`);
  }, [navigate]);

  // Memoized helpers
  const getTypeLabel = useCallback((type: string) => {
    const types: { [key: string]: string } = {
      'producer': '🟢 Produzent',
      'trader': '🟠 Händler',
      'trader_producer': '🟣 Händler & Produzent',
      'non_food': '🔵 Non-Food',
      'gmo_declaration': '🧬 GVO-Erklärung',
      'conformity_declaration': '📋 Konformitätserklärung',
      'emergency_contact': '🚨 Notfallkontakt'
    };
    return types[type] || `❓ ${type}`;
  }, []);

  // Get template title based on its language
  const getTemplateTitle = useCallback((template: QuestionnaireTemplate): string => {
    // Use the title in the template's language if available, otherwise fallback to DE
    if (template.language === 'en' && template.title.en) {
      return template.title.en;
    }
    return template.title.de || template.title.en || 'Untitled';
  }, []);

  // Get template description based on its language
  const getTemplateDescription = useCallback((template: QuestionnaireTemplate): string => {
    // Use the description in the template's language if available, otherwise fallback to DE
    if (template.language === 'en' && template.description.en) {
      return template.description.en;
    }
    return template.description.de || template.description.en || '';
  }, []);

  // Memoized filtered templates
  const filteredTemplates = useMemo(() => {
    let filtered = templates.filter(template => {
      const matchesSearch = getTemplateTitle(template).toLowerCase().includes(searchTerm.toLowerCase()) ||
                           getTemplateDescription(template).toLowerCase().includes(searchTerm.toLowerCase());
      
      // **SPECIAL HANDLING FOR SYSTEM TEMPLATES**
      // Only show conformity declarations when explicitly filtered for them
      if (template.type === 'conformity_declaration') {
        return matchesSearch && filterType === 'conformity_declaration';
      }
      
      // Only show emergency contacts when explicitly filtered for them
      if (template.type === 'emergency_contact') {
        return matchesSearch && filterType === 'emergency_contact';
      }
      
      // For regular templates, exclude system templates from "all" and other filters
      const matchesType = filterType === 'all' || template.type === filterType;
      const matchesStatus = filterStatus === 'all' || filterStatus === 'active'
                           ? (filterStatus === 'all' ? true : template.isActive)
                           : !template.isActive; // inactive
      
      return matchesSearch && matchesType && matchesStatus;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.createdAt!).getTime() - new Date(b.createdAt!).getTime();
        case 'name':
          return getTemplateTitle(a).localeCompare(getTemplateTitle(b));
        case 'questions':
          return b.questions.length - a.questions.length;
        case 'newest':
        default:
          return new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime();
      }
    });

    return filtered;
  }, [templates, searchTerm, filterType, filterStatus, sortBy, getTemplateTitle, getTemplateDescription]);

  if (loading) {
    return <LoadingSpinner size="medium" text="Fragebögen werden geladen..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* **KOMPAKTE TOOLBAR** */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          {/* **SUCHE** */}
          <div className="relative flex-1">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-2.5 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Fragebögen durchsuchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white text-sm"
            />
          </div>
          
          {/* **FILTER UND AKTIONEN** */}
          <div className="flex gap-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white text-sm min-w-[120px]"
            >
              <option value="all">Alle Typen</option>
              <option value="producer">🟢 Produzent</option>
              <option value="trader">🟠 Händler</option>
              <option value="trader_producer">🟣 Händler & Produzent</option>
              <option value="non_food">🔵 Non-Food</option>
              <option value="gmo_declaration">🧬 GVO-Erklärung</option>
              <option value="conformity_declaration">📋 Konformitätserklärung</option>
              <option value="emergency_contact">🚨 Notfallkontakt</option>
            </select>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white text-sm min-w-[100px]"
            >
              <option value="all">Alle Status</option>
              <option value="active">Aktiv</option>
              <option value="inactive">Inaktiv</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white text-sm min-w-[100px]"
            >
              <option value="newest">Neueste</option>
              <option value="oldest">Älteste</option>
              <option value="name">Name</option>
              <option value="questions">Fragen</option>
            </select>
            
            <Link
              to="/questionnaires/create"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1.5 text-xs" />
              Neuer Fragebogen
            </Link>
          </div>
        </div>
      </div>

      {/* **ERROR MESSAGE** */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.15 }}
            className="px-4 pt-4"
          >
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md flex items-center justify-between text-sm">
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-3 text-red-500 hover:text-red-700"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* **CONTENT BEREICH** */}
      <div className="p-4">
        {filteredTemplates.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <div className="text-gray-400 mb-4">
              <FontAwesomeIcon icon={faClipboardList} className="text-4xl mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchTerm || filterType !== 'all' || filterStatus !== 'all' 
                  ? 'Keine Fragebögen gefunden' 
                  : 'Noch keine Fragebögen vorhanden'}
              </h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                {searchTerm || filterType !== 'all' || filterStatus !== 'all'
                  ? 'Versuchen Sie andere Suchkriterien oder Filter'
                  : 'Erstellen Sie Ihren ersten Fragebogen und beginnen Sie mit der Lieferantenbewertung'}
              </p>
            </div>
            
            {(!searchTerm && filterType === 'all' && filterStatus === 'all') && (
              <Link
                to="/questionnaires/create"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Ersten Fragebogen erstellen
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {filteredTemplates.map((template, index) => {
              const isHighlighted = highlightId === template._id;
              
              return (
                <motion.div
                  key={template._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0, scale: isHighlighted ? 1.02 : 1 }}
                  transition={{ delay: index * 0.03 }}
                  className={`bg-white rounded-lg shadow-sm border ${
                    isHighlighted 
                      ? 'border-blue-500 ring-2 ring-blue-200' 
                      : 'border-gray-200'
                  } hover:shadow-md transition-all duration-200 cursor-pointer group`}
                  onClick={() => handleTemplateClick(template)}
                  onAnimationComplete={() => {
                    // Remove highlight after animation
                    if (isHighlighted) {
                      setTimeout(() => setHighlightId(undefined), 2000);
                    }
                  }}
                >
                  <div className="p-4">
                  {/* **HEADER** */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center mb-1">
                        <span className="text-base mr-1.5">
                          {getTypeLabel(template.type).split(' ')[0]}
                        </span>
                        <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${
                          template.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {template.isActive ? 'Aktiv' : 'Inaktiv'} • v{template.version}
                        </span>
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {getTemplateTitle(template)}
                      </h3>
                      <p className="text-gray-600 text-xs line-clamp-2 mb-3">
                        {getTemplateDescription(template)}
                      </p>
                    </div>
                  </div>

                  {/* **STATS** */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                    <span className="flex items-center">
                      <span className="font-medium text-gray-900">
                        {template.questions.filter(q => q.type !== 'section').length}
                      </span>
                      <span className="ml-1">Fragen</span>
                    </span>
                    <span>
                      {new Date(template.createdAt || '').toLocaleDateString('de-DE')}
                    </span>
                  </div>

                  {/* **ACTIONS** */}
                  <div className="grid grid-cols-3 gap-1 mb-1">
                    <button
                      onClick={(e) => handlePreview(template, e)}
                      className="flex items-center justify-center px-2 py-1.5 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 font-medium transition-colors"
                      title="Vorschau"
                    >
                      <FontAwesomeIcon icon={faEye} className="mr-1 text-xs" />
                      Vorschau
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/questionnaires/edit/${template._id}`);
                      }}
                      className="flex items-center justify-center px-2 py-1.5 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 font-medium transition-colors"
                      title="Bearbeiten"
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-1 text-xs" />
                      Bearbeiten
                    </button>
                    
                    {/* Senden Button - nicht für Konformitätserklärungen */}
                    {template.type !== 'conformity_declaration' && (
                      <button
                        onClick={(e) => handleSendClick(template, e)}
                        className="flex items-center justify-center px-2 py-1.5 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100 font-medium transition-colors"
                        title="Senden"
                      >
                        <FontAwesomeIcon icon={faPaperPlane} className="mr-1 text-xs" />
                        Senden
                      </button>
                    )}
                    
                    {/* Placeholder für Konformitätserklärungen */}
                    {template.type === 'conformity_declaration' && (
                      <div className="flex items-center justify-center px-2 py-1.5 text-xs bg-gray-100 text-gray-400 rounded font-medium">
                        <FontAwesomeIcon icon={faShieldAlt} className="mr-1 text-xs" />
                        Geschützt
                      </div>
                    )}
                  </div>

                  {/* **SECONDARY ACTIONS** */}
                  <div className="grid grid-cols-3 gap-1">
                    <button
                      onClick={(e) => handleShowVersionHistory(template, e)}
                      className="flex items-center justify-center px-2 py-1.5 text-xs bg-orange-50 text-orange-700 rounded hover:bg-orange-100 font-medium transition-colors"
                      title="Versionen"
                    >
                      <FontAwesomeIcon icon={faHistory} className="mr-1 text-xs" />
                      Versionen
                    </button>
                    
                    {/* Kopieren Button - nicht für Konformitätserklärungen */}
                    {template.type !== 'conformity_declaration' && (
                      <button
                        onClick={(e) => handleDuplicate(template, e)}
                        className="flex items-center justify-center px-2 py-1.5 text-xs bg-gray-50 text-gray-700 rounded hover:bg-gray-100 font-medium transition-colors"
                        title="Duplizieren"
                      >
                        <FontAwesomeIcon icon={faCopy} className="mr-1 text-xs" />
                        Kopieren
                      </button>
                    )}
                    
                    {/* Placeholder für Konformitätserklärungen */}
                    {template.type === 'conformity_declaration' && (
                      <div className="flex items-center justify-center px-2 py-1.5 text-xs bg-gray-100 text-gray-400 rounded font-medium">
                        <FontAwesomeIcon icon={faShieldAlt} className="mr-1 text-xs" />
                        Geschützt
                      </div>
                    )}
                    
                    {/* Delete button - nur anzeigen wenn löschbar */}
                    {(!template.isSystemTemplate && template.isDeletable !== false) && (
                      <button
                        onClick={(e) => handleDelete(template, e)}
                        className="flex items-center justify-center px-2 py-1.5 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100 font-medium transition-colors"
                        title="Löschen"
                      >
                        <FontAwesomeIcon icon={faTrash} className="mr-1 text-xs" />
                        Löschen
                      </button>
                    )}
                    
                    {/* Placeholder wenn Delete-Button versteckt ist */}
                    {(template.isSystemTemplate || template.isDeletable === false) && (
                      <div className="flex items-center justify-center px-2 py-1.5 text-xs bg-gray-100 text-gray-400 rounded font-medium">
                        <FontAwesomeIcon icon={faShieldAlt} className="mr-1 text-xs" />
                        Geschützt
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
            })}
          </div>
        )}
      </div>

      {/* **MODALS** */}
      <AnimatePresence>
        {showPreview && selectedTemplate && (
          <PreviewModal
            template={selectedTemplate}
            language="de"
            onClose={handleClosePreview}
          />
        )}
        
        {showSendModal && selectedTemplate && (
          <SendModal
            template={selectedTemplate}
            onClose={handleCloseSendModal}
            onSuccess={loadData}
          />
        )}
        
        {showDeleteModal && templateToDelete && (
          <DeleteModal
            templateTitle={getTemplateTitle(templateToDelete)}
            onClose={handleCloseDeleteModal}
            onConfirm={() => handleConfirmDelete(false)}
          />
        )}
        
        {showForceDeleteModal && templateToDelete && deleteWarningData && (
          <ForceDeleteModal
            templateTitle={getTemplateTitle(templateToDelete)}
            warningData={deleteWarningData}
            onClose={handleCloseDeleteModal}
            onConfirm={() => handleConfirmDelete(true)}
          />
        )}
        
        {/* Force Delete Modal with separate states */}
        {forceDeleteModalVisible && forceDeleteTemplate && forceDeleteData && (
          <ForceDeleteModal
            templateTitle={getTemplateTitle(forceDeleteTemplate)}
            warningData={forceDeleteData}
            onClose={() => {
              setForceDeleteModalVisible(false);
              setForceDeleteData(null);
              setForceDeleteTemplate(null);
            }}
            onConfirm={async () => {
              try {
                const url = `${forceDeleteTemplate._id}?force=true`;
                await deleteQuestionnaireTemplate(url);
                await loadData();
                
                // Alle States zurücksetzen
                setForceDeleteModalVisible(false);
                setForceDeleteData(null);
                setForceDeleteTemplate(null);
                setShowDeleteModal(false);
                setShowForceDeleteModal(false);
                setDeleteWarningData(null);
                setTemplateToDelete(null);
                
                console.log('Template and related submissions deleted successfully');
              } catch (error) {
                console.error('Error force deleting template:', error);
              }
            }}
          />
        )}
        
        {/* Debug: Simple modal to test state */}
        {false && forceDeleteModalVisible && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white p-6 rounded-lg" onClick={(e) => e.stopPropagation()}>
              <h3>DEBUG: Force Delete Modal State</h3>
              <p>forceDeleteModalVisible: {String(forceDeleteModalVisible)}</p>
              <p>showForceDeleteModal: {String(showForceDeleteModal)}</p>
              <p>templateToDelete: {templateToDelete ? 'Yes' : 'No'}</p>
              <p>deleteWarningData: {deleteWarningData ? 'Yes' : 'No'}</p>
              <p><strong>forceDeleteTemplate: {forceDeleteTemplate ? 'Yes' : 'No'}</strong></p>
              <p><strong>forceDeleteData: {forceDeleteData ? 'Yes' : 'No'}</strong></p>
              <button 
                onClick={() => {
                  setForceDeleteModalVisible(false);
                  setShowForceDeleteModal(false);
                  setTemplateToDelete(null);
                  setDeleteWarningData(null);
                  setForceDeleteData(null);
                  setForceDeleteTemplate(null);
                }}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
              >
                Close Debug
              </button>
            </div>
          </div>
        )}
        
        {showVersionHistory && selectedTemplateForVersions && (
          <VersionHistory
            templateId={selectedTemplateForVersions._id!}
            templateTitle={getTemplateTitle(selectedTemplateForVersions)}
            currentVersion={selectedTemplateForVersions.version}
            onClose={handleCloseVersionHistory}
            onVersionRestored={handleVersionRestored}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Questionnaires;